import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Plus, Search, Building2, Calendar, User, Clock,
  Edit, Eye, Filter, CheckCircle, XCircle, AlertCircle, FileText,
  Phone, Mail, MapPin, Wrench, Droplet, Zap, Home
} from 'lucide-react';

const Issues = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedIssue, setSelectedIssue] = useState(null);

  const statusOptions = ['all', 'open', 'in-progress', 'resolved', 'closed'];
  const priorityOptions = ['all', 'low', 'medium', 'high', 'urgent'];
  const typeOptions = ['all', 'maintenance', 'complaint', 'request', 'emergency'];

  // Mock issues data
  const issues = [
    {
      id: 'ISS-001',
      title: 'Roof Leak in Warehouse',
      description: 'Water leaking from roof during heavy rain, affecting stored goods',
      propertyId: 'PROP-002',
      propertyName: 'Jeddah Warehouse Complex',
      propertyInternalId: 'LOC-JED-001',
      tenantId: 'TEN-002',
      tenantName: 'XYZ Logistics',
      tenantContact: '+966 50 987 6543',
      type: 'maintenance',
      priority: 'high',
      status: 'in-progress',
      reportedDate: '2024-01-25',
      reportedBy: 'Mohammed Ali',
      assignedTo: 'Maintenance Team A',
      dueDate: '2024-02-05',
      resolvedDate: null,
      estimatedCost: 5000,
      actualCost: null,
      notes: 'Urgent repair needed before next rainy season',
      attachments: []
    },
    {
      id: 'ISS-002',
      title: 'AC Not Working',
      description: 'Air conditioning system not functioning properly in office building',
      propertyId: 'PROP-003',
      propertyName: 'Dammam Office Building',
      propertyInternalId: 'LOC-DMM-001',
      tenantId: 'TEN-003',
      tenantName: 'Tech Solutions Inc.',
      tenantContact: '+966 50 555 1234',
      type: 'maintenance',
      priority: 'medium',
      status: 'open',
      reportedDate: '2024-02-01',
      reportedBy: 'Omar Hassan',
      assignedTo: null,
      dueDate: '2024-02-15',
      resolvedDate: null,
      estimatedCost: 2000,
      actualCost: null,
      notes: 'Tenant reporting discomfort due to heat',
      attachments: []
    },
    {
      id: 'ISS-003',
      title: 'Parking Space Dispute',
      description: 'Tenant complaining about unauthorized vehicles using assigned parking spaces',
      propertyId: 'PROP-003',
      propertyName: 'Dammam Office Building',
      propertyInternalId: 'LOC-DMM-001',
      tenantId: 'TEN-003',
      tenantName: 'Tech Solutions Inc.',
      tenantContact: '+966 50 555 1234',
      type: 'complaint',
      priority: 'low',
      status: 'resolved',
      reportedDate: '2024-01-20',
      reportedBy: 'Omar Hassan',
      assignedTo: 'Property Manager',
      dueDate: '2024-01-25',
      resolvedDate: '2024-01-24',
      estimatedCost: 0,
      actualCost: 0,
      notes: 'Resolved by installing parking barriers',
      attachments: []
    },
    {
      id: 'ISS-004',
      title: 'Electrical Panel Upgrade Request',
      description: 'Tenant requesting upgrade to electrical panel for increased capacity',
      propertyId: 'PROP-005',
      propertyName: 'Khobar Storage Facility',
      propertyInternalId: 'LOC-KBU-001',
      tenantId: 'TEN-004',
      tenantName: 'Storage Co.',
      tenantContact: '+966 50 444 5678',
      type: 'request',
      priority: 'medium',
      status: 'open',
      reportedDate: '2024-01-28',
      reportedBy: 'Khalid Omar',
      assignedTo: null,
      dueDate: '2024-03-01',
      resolvedDate: null,
      estimatedCost: 15000,
      actualCost: null,
      notes: 'Major upgrade, requires approval and planning',
      attachments: []
    },
    {
      id: 'ISS-005',
      title: 'Water Pipe Burst',
      description: 'Emergency: Water pipe burst in basement, flooding reported',
      propertyId: 'PROP-002',
      propertyName: 'Jeddah Warehouse Complex',
      propertyInternalId: 'LOC-JED-001',
      tenantId: 'TEN-002',
      tenantName: 'XYZ Logistics',
      tenantContact: '+966 50 987 6543',
      type: 'emergency',
      priority: 'urgent',
      status: 'resolved',
      reportedDate: '2024-01-30',
      reportedBy: 'Mohammed Ali',
      assignedTo: 'Emergency Response Team',
      dueDate: '2024-01-30',
      resolvedDate: '2024-01-30',
      estimatedCost: 8000,
      actualCost: 7500,
      notes: 'Emergency repair completed same day',
      attachments: []
    },
  ];

  const filteredIssues = issues.filter(issue => {
    const matchesSearch = issue.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      issue.propertyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      issue.tenantName.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesFilter = true;
    if (selectedFilter !== 'all') {
      matchesFilter = issue.status === selectedFilter;
    }
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in-progress': return 'bg-orange-100 text-orange-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'maintenance': return Wrench;
      case 'complaint': return AlertCircle;
      case 'request': return FileText;
      case 'emergency': return AlertTriangle;
      default: return AlertTriangle;
    }
  };

  const IssueCard = ({ issue }) => {
    const TypeIcon = getTypeIcon(issue.type);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer"
        onClick={() => setSelectedIssue(issue)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded-lg ${getPriorityColor(issue.priority)}`}>
              <TypeIcon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{issue.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                  {issue.status}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(issue.priority)}`}>
                  {issue.priority}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{issue.propertyName}</p>
              <p className="text-sm text-gray-600 mb-4">{issue.tenantName}</p>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Type</span>
                  <span className="text-sm text-gray-900 capitalize">{issue.type}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Reported Date</span>
                  <span className="text-sm text-gray-900">{issue.reportedDate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Reported By</span>
                  <span className="text-sm text-gray-900">{issue.reportedBy}</span>
                </div>
                {issue.assignedTo && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Assigned To</span>
                    <span className="text-sm text-gray-900">{issue.assignedTo}</span>
                  </div>
                )}
                {issue.dueDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Due Date</span>
                    <span className="text-sm text-gray-900">{issue.dueDate}</span>
                  </div>
                )}
                {issue.estimatedCost > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Estimated Cost</span>
                    <span className="text-sm font-semibold text-gray-900">SAR {issue.estimatedCost.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Issues & Tickets</h2>
            <p className="text-sm text-gray-600 mt-1">
              Track and manage property issues, maintenance requests, and complaints
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search issues..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm font-medium">
              <Plus className="w-4 h-4" />
              New Issue
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <button
              key={status}
              onClick={() => setSelectedFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === status
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Issues</p>
              <p className="text-2xl font-bold text-gray-900">{issues.length}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Open</p>
              <p className="text-2xl font-bold text-blue-600">
                {issues.filter(i => i.status === 'open').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-orange-600">
                {issues.filter(i => i.status === 'in-progress').length}
              </p>
            </div>
            <Wrench className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-green-600">
                {issues.filter(i => i.status === 'resolved').length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Issues Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredIssues.map((issue) => (
          <IssueCard key={issue.id} issue={issue} />
        ))}
      </div>

      {/* Issue Detail Modal */}
      {selectedIssue && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Issue Details</h3>
              <button
                onClick={() => setSelectedIssue(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Issue Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Title</label>
                  <p className="text-lg text-gray-900">{selectedIssue.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedIssue.status)}`}>
                    {selectedIssue.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <p className="text-lg text-gray-900 capitalize">{selectedIssue.type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Priority</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedIssue.priority)}`}>
                    {selectedIssue.priority}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Property</label>
                  <p className="text-lg text-gray-900">{selectedIssue.propertyName}</p>
                  <p className="text-sm text-gray-600">{selectedIssue.propertyInternalId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Tenant</label>
                  <p className="text-lg text-gray-900">{selectedIssue.tenantName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Reported Date</label>
                  <p className="text-lg text-gray-900">{selectedIssue.reportedDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Reported By</label>
                  <p className="text-lg text-gray-900">{selectedIssue.reportedBy}</p>
                </div>
                {selectedIssue.assignedTo && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Assigned To</label>
                    <p className="text-lg text-gray-900">{selectedIssue.assignedTo}</p>
                  </div>
                )}
                {selectedIssue.dueDate && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Due Date</label>
                    <p className="text-lg text-gray-900">{selectedIssue.dueDate}</p>
                  </div>
                )}
                {selectedIssue.resolvedDate && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Resolved Date</label>
                    <p className="text-lg text-green-600">{selectedIssue.resolvedDate}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="border-t border-gray-200 pt-6">
                <label className="text-sm font-medium text-gray-700">Description</label>
                <p className="text-lg text-gray-900 mt-2">{selectedIssue.description}</p>
              </div>

              {/* Cost Information */}
              {(selectedIssue.estimatedCost > 0 || selectedIssue.actualCost) && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Cost Information</h4>
                  <div className="grid grid-cols-2 gap-6">
                    {selectedIssue.estimatedCost > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Estimated Cost</label>
                        <p className="text-lg font-semibold text-gray-900">SAR {selectedIssue.estimatedCost.toLocaleString()}</p>
                      </div>
                    )}
                    {selectedIssue.actualCost !== null && (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Actual Cost</label>
                        <p className="text-lg font-semibold text-gray-900">SAR {selectedIssue.actualCost.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tenant Contact */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Tenant Contact</h4>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-lg text-gray-900">{selectedIssue.tenantContact}</p>
                  </div>
                </div>
              </div>

              {selectedIssue.notes && (
                <div className="border-t border-gray-200 pt-6">
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-lg text-gray-900 mt-2">{selectedIssue.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6 flex gap-3">
                {selectedIssue.status !== 'resolved' && selectedIssue.status !== 'closed' && (
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Mark as Resolved
                  </button>
                )}
                <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit Issue
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  View Property
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Issues;
